import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Shield,
  Phone,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Copy,
  MessageCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnonymousHelp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form, submitted, chat
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [callback, setCallback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please describe what you need help with");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/anonymous/request`, {
        description: description.trim(),
        urgency: urgency,
        preferred_contact: callback || null
      });
      
      setSessionInfo(response.data);
      setStep("submitted");
      toast.success("Your request has been submitted");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copySessionId = () => {
    if (sessionInfo?.anonymous_id) {
      navigator.clipboard.writeText(sessionInfo.anonymous_id);
      toast.success("Session ID copied to clipboard");
    }
  };

  const checkStatus = async () => {
    if (!sessionInfo?.anonymous_id) return;
    
    try {
      const response = await axios.get(`${API}/anonymous/status/${sessionInfo.anonymous_id}`);
      if (response.data.responder_assigned) {
        toast.success("A responder has been assigned! You can start chatting.");
        setStep("chat");
      } else {
        toast.info("Still waiting for a responder. Please check back shortly.");
      }
    } catch (error) {
      toast.error("Could not check status");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/quick-help")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <EyeOff className="w-5 h-5 text-primary" />
                  Anonymous Help
                </h1>
                <p className="text-sm text-muted-foreground">
                  Your identity is protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Privacy Banner */}
      <div className="bg-green-50 border-b border-green-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Your privacy is protected.</strong> No login required. Your name and identity 
            will not be shared with responders unless you choose to reveal it.
          </p>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Crisis Warning */}
        <Card className="card-valor mb-6 border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-800 font-medium">
                If you're in immediate danger, please call:
              </p>
              <div className="flex gap-4 mt-2">
                <a href="tel:988" className="text-red-700 font-bold hover:underline">
                  988 (Veterans: Press 1)
                </a>
                <a href="tel:911" className="text-red-700 font-bold hover:underline">
                  911 Emergency
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {step === "form" && (
          <Card className="card-valor">
            <CardHeader>
              <CardTitle>Request Anonymous Support</CardTitle>
              <CardDescription>
                Tell us what you're going through. A peer responder will be notified.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">What do you need help with? *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Share what's on your mind. Our responders are here to listen and support you..."
                  rows={5}
                  data-testid="anonymous-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="urgency">How urgent is this?</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger data-testid="anonymous-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Just need someone to talk to</SelectItem>
                    <SelectItem value="medium">Medium - Struggling but managing</SelectItem>
                    <SelectItem value="high">High - Need support soon</SelectItem>
                    <SelectItem value="crisis">Crisis - Need help now</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="callback">Callback number (optional)</Label>
                <Input
                  id="callback"
                  type="tel"
                  value={callback}
                  onChange={(e) => setCallback(e.target.value)}
                  placeholder="If you'd like a responder to call you"
                  data-testid="anonymous-callback"
                />
                <p className="text-xs text-muted-foreground">
                  Only shared with your assigned responder if you provide it
                </p>
              </div>

              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !description.trim()}
                className="w-full"
                data-testid="submit-anonymous-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Anonymous Request"}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "submitted" && sessionInfo && (
          <Card className="card-valor">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle>Request Submitted</CardTitle>
                  <CardDescription>
                    Responders have been notified
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Your Anonymous Session ID</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-lg font-mono font-bold">{sessionInfo.anonymous_id}</code>
                  <Button variant="ghost" size="icon" onClick={copySessionId}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Save this ID to check your status or continue your conversation later
                </p>
              </div>

              <div className="space-y-2">
                <Button onClick={checkStatus} variant="outline" className="w-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Check for Responder
                </Button>
                
                <Button onClick={() => navigate("/quick-help")} variant="ghost" className="w-full">
                  Back to Quick Help
                </Button>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-medium text-blue-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Available responders have been notified</li>
                  <li>• A responder will accept your request soon</li>
                  <li>• You can chat anonymously once connected</li>
                  <li>• You can reveal your identity later if you choose</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "chat" && sessionInfo && (
          <Card className="card-valor">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                Anonymous Chat
              </CardTitle>
              <CardDescription>
                A responder is ready to help you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-muted-foreground">
                  Chat feature coming soon. For now, please use the main Quick Help 
                  page or call the crisis line if you need immediate support.
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate("/quick-help")}
                >
                  Go to Quick Help
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => window.location.href = "tel:988"}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call 988
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Privacy Assurance */}
        <div className="mt-8 text-center">
          <Badge variant="outline" className="mb-2">
            <Shield className="w-3 h-3 mr-1" />
            Privacy Protected
          </Badge>
          <p className="text-sm text-muted-foreground">
            Responders will see you as "Anonymous Veteran" until you choose to share your identity.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AnonymousHelp;
